import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { numberOfProducts } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import type { FormattedCourse } from 'bundles/enroll-common/types/enrollSectionTypes';
import { isFreemiumModelRolloutGAEnabled } from 'bundles/enroll-common/utils/freemiumModelUtils';
import type OnDemandSpecializationsV1 from 'bundles/naptimejs/resources/onDemandSpecializations.v1';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import type { Price } from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';
import { ProfessionalCertificateS12n } from 'bundles/s12n-common/constants/s12nProductVariants';
import type { S12nProductVariant } from 'bundles/s12n-common/constants/s12nProductVariants';
import { getS12nDisplayName } from 'bundles/s12n-common/lib/s12nProductVariantUtils';

import _t from 'i18n!nls/coursera-plus-common';

const getFreeTrialValueProps = ({ s12nProductVariant }: { s12nProductVariant?: S12nProductVariant }) => {
  const UNLIMITED_BULLET_HEADER_CERTIFICATE = _t('Unlimited access to all courses in this Professional Certificate.');
  const UNLIMITED_BULLET_HEADER_S12N = _t('Unlimited access to all courses in this Specialization.');
  const UNLIMITED_BULLET = {
    header:
      s12nProductVariant === ProfessionalCertificateS12n
        ? UNLIMITED_BULLET_HEADER_CERTIFICATE
        : UNLIMITED_BULLET_HEADER_S12N,
    subheader: _t('Watch lectures, try assignments, participate in discussion forums, and more.'),
  };

  const CANCEL_ANYTIME_BULLET = {
    header: _t('Cancel anytime.'),
    subheader: _t("No penalties - simply cancel before the trial ends if it's not right for you."),
  };

  const MONTHLY_PRICE_BULLET = {
    header: _t('{monthlyPrice}/month to continue learning after your trial ends.'),
    subheader: _t('Enjoy the freedom to learn at your own pace.'),
  };

  const certificateValuePropHeader = isFreemiumModelRolloutGAEnabled()
    ? _t('Certificate when you complete after your first payment.')
    : _t('Certificate when you complete after your trial ends.');

  const CERTIFICATE_BULLET = {
    header: certificateValuePropHeader,
    subheader: _t('Share on your resume, LinkedIn, and CV.'),
  };

  const FREE_TRIAL_LOGGED_IN_BULLETS = [
    UNLIMITED_BULLET,
    CANCEL_ANYTIME_BULLET,
    MONTHLY_PRICE_BULLET,
    CERTIFICATE_BULLET,
  ];

  return {
    FREE_TRIAL_LOGGED_IN_BULLETS,
  };
};

const getPaidSubscriptionValueProps = () => [
  _t('Practice material, graded assignments, discussion forums, and more'),
  _t('Certificates to share on your resume, Linkedin, or CV'),
  _t('24/7 customer support'),
  _t('Easy cancellation'),
];

export const getS12nEnrollModalCopies = ({
  s12nSubscriptionPrice,
  s12nProductVariant,
  course,
  s12n,
}: {
  s12nSubscriptionPrice?: Price;
  s12nProductVariant?: S12nProductVariant;
  course: FormattedCourse;
  s12n: OnDemandSpecializationsV1;
}) => {
  return {
    freeTrialTitle: _t('Start your 7-day free trial'),
    paidTitle: s12nSubscriptionPrice && (
      <FormattedMessage
        message={_t('{price} per month')}
        price={
          <ReactPriceDisplay
            currency={s12nSubscriptionPrice.currencyCode}
            value={s12nSubscriptionPrice.amount}
            hideCurrencyCode={true}
          />
        }
      />
    ),
    freeTrialCta: _t('Start free trial'),
    paidCta: _t('Subscribe'),
    secondaryCta: _t('View all plans'),
    freeTrialValueProps: getFreeTrialValueProps({ s12nProductVariant }),
    paidValueProps: getPaidSubscriptionValueProps(),
    freeTrialSubtitle: (
      <FormattedMessage
        message={_t('{courseName} is part of the larger {productName}. Your 7-day free trial includes:')}
        courseName={course.name}
        productName={getS12nDisplayName(s12n.name, s12n.productVariant)}
      />
    ),
    paidS12nSubtitle: _t('Subscribe to the entire series and get access to:'),
    paidCourseSubtitle: (
      <FormattedMessage
        message={_t("{courseName} is part of the larger {s12nName} series. When you subscribe you'll get access to:")}
        s12nName={s12n.name}
        courseName={<strong>{course.name}</strong>}
      />
    ),
  };
};

export const getAllPlansModalCopy = () => {
  return {
    back: _t('Back'),
    mostPopularTag: _t('Most popular'),
    pageHeader: _t('Choose a plan that works for you'),
    singleProgramTitle: _t('Single program'),
    freeTrialEnrollCta: _t('Start free trial'),
    freeTrialEnrollCtaCPlusMonthlyA11y: _t('Start Coursera Plus monthly free trial'),
    freeTrialEnrollCtaCPlusAnnualA11y: _t('Start Coursera Plus annual free trial'),
    freeTrialEnrollCtaSingleProgramA11y: _t('Start free trial in single program'),
    paidEnrollCta: _t('Enroll'),
    paidEnrollCtaCPlusMonthlyA11y: _t('Enroll in Coursera Plus monthly'),
    paidEnrollCtaCPlusAnnualA11y: _t('Enroll in Coursera Plus annual'),
    paidEnrollCtaSingleProgramA11y: _t('Enroll in single program'),
  };
};

export const getCourseraPlusModalCopies = ({
  courseraPlusPrice,
  hasCourseraPlusFreeTrial,
}: {
  courseraPlusPrice: {
    amount: number;
    currencyCode: string;
  };
  hasCourseraPlusFreeTrial: boolean;
}) => {
  return {
    headline: hasCourseraPlusFreeTrial ? _t('Start your 7-day free trial') : _t('Renew your subscription'),
    valueProps: [
      {
        header: (
          <FormattedMessage
            message={_t('Access to {numberOfProducts} learning programs')}
            numberOfProducts={numberOfProducts}
          />
        ),
        subheader: _t('Explore courses, hands-on projects, and certificates at no additional cost.'),
      },
      {
        header: hasCourseraPlusFreeTrial
          ? _t('Coursera Coach by your side after your trial ends')
          : _t('Coursera Coach by your side'),
        subheader: _t(
          'Grasp complex concepts, get unstuck, and test your knowledge with Coursera Coach, your AI-powered guide.'
        ),
      },
      {
        header: hasCourseraPlusFreeTrial
          ? _t('A certificate for every program you complete after your trial ends')
          : _t('A certificate for every program you complete'),
        subheader: _t('Share on your resume, LinkedIn, and CV'),
      },
      {
        header: (
          <FormattedMessage
            message={hasCourseraPlusFreeTrial ? _t('{price}/month after your trial') : _t('{price}/month')}
            price={
              <ReactPriceDisplay
                currency={courseraPlusPrice.currencyCode}
                value={courseraPlusPrice.amount}
                hideCurrencyCode={true}
              />
            }
          />
        ),
        subheader: _t('Cancel anytime — no penalties.'),
      },
    ],
    primaryCta: hasCourseraPlusFreeTrial ? _t('Start free trial') : _t('Renew subscription'),
    secondaryCta: _t('View all plans'),
  };
};
